import { type FC, useMemo } from "react";
import { generatePath } from "react-router-dom";

import { Spinner } from "@snapchat/snap-design-system";

import { useAssistantLensStatsContext } from "../../assistant-chat/hooks";
import { AbsoluteRoute } from "../../core/routes";
// import { RouteEntities } from "../../core/routes/relativeRoutes";
import { ExclusiveLensStatus, type GoogleProtobufTimestamp } from "../../gql-generated/types";
// import { RemovedExclusiveLensAlert } from "../../lens-creator-payout/components/RemovedExclusiveLensAlert";
/*
import {
    LensPlusScore,
    LensPlusScoreTooltip,
    LensPlusSubscriptionsEarned,
    LensPlusSubscriptionsEarnedTooltip,
    TotalLensPlusAudience,
    TotalLensPlusAudienceTooltip,
} from "../../lens-creator-payout/components/messages.generated";
*/
import { usePayoutDriverStats } from "../../lens-creator-payout/hooks/usePayoutDriverStats";
import { LensGranularity } from "../../lens-insights/models";
import { getLensPlusExclusivityCutoff } from "../../lens-insights/utils/lensPlusDateRanges";
import { Flexbox } from "../../shared/components";
import { useSelectedOrganizationContainer } from "../../shared/hooks";
import { LinkStyled, linkButtonElevatedClassName } from "../../shared/styles";
import { useOrganicLensStats } from "../hooks";

import { container, header, layout, spinnerSize, statValue } from "./LensStats.module.css";
import { LensStatsItem } from "./LensStatsItem";
import {
    LensInsightsLinkEngagement,
    // LensInsightsLinkPayout,
    LensStatsEngagementSection,
    // LensStatsMonetizationSection,
} from "./messages.generated";

interface LensStatsProps {
    lensId: string;
    unlockableId: string;
    exclusiveLensStatus: ExclusiveLensStatus;
    markedAsExclusiveAt?: GoogleProtobufTimestamp | null;
}

export const LensStats: FC<LensStatsProps> = ({ lensId, unlockableId, exclusiveLensStatus, markedAsExclusiveAt }) => {
    const { orgId, lensFolderId } = useSelectedOrganizationContainer();
    const { loading, error, stats } = useOrganicLensStats(lensId);

    const isRemovedFromLensPlus = exclusiveLensStatus === ExclusiveLensStatus.ExclusiveLensStatusRemoved;
    const lensPlusCutoff = getLensPlusExclusivityCutoff(exclusiveLensStatus, markedAsExclusiveAt);
    const payoutStats = usePayoutDriverStats({
        unlockableId,
        timeRange: LensGranularity.Lifetime,
        markedAsExclusiveAt: lensPlusCutoff,
        skip: isRemovedFromLensPlus,
    });

    const organicError = error !== undefined;
    const assistantOrganicStats = useMemo(() => stats.map(({ label, value }) => ({ label, value })), [stats]);
    const assistantLensStatsInput = useMemo(
        () => ({
            lensKey: lensId,
            organicStats: assistantOrganicStats,
            organicLoading: loading,
            organicError,
            payoutStats: {
                subscriptionsEarned: { value: payoutStats.subscriptionsEarned.value },
                audience: { value: payoutStats.audience.value },
                score: { value: payoutStats.score.value },
                payout: { value: payoutStats.payout.value },
                loading: payoutStats.loading,
            },
        }),
        [
            lensId,
            assistantOrganicStats,
            loading,
            organicError,
            payoutStats.subscriptionsEarned.value,
            payoutStats.audience.value,
            payoutStats.score.value,
            payoutStats.payout.value,
            payoutStats.loading,
        ],
    );
    useAssistantLensStatsContext(assistantLensStatsInput);

    if (loading) {
        return (
            <Flexbox justifyContent="center" alignItems="center" className={spinnerSize}>
                <Spinner />
            </Flexbox>
        );
    }

    const queryString = new URLSearchParams({ lensId }).toString();

    const engagementBasePath =
        orgId && lensFolderId
            ? generatePath(AbsoluteRoute.ORG_LENS_FOLDER_INSIGHTS, { orgId, lensFolderId })
            : AbsoluteRoute.INSIGHTS;

    /*
    const monetizationBasePath =
        orgId && lensFolderId
            ? generatePath(AbsoluteRoute.ORG_LENS_FOLDER_INSIGHTS_TAB, {
                  orgId,
                  lensFolderId,
                  insightsTab: RouteEntities.Monetization,
              })
            : generatePath(AbsoluteRoute.INSIGHTS_TAB, { insightsTab: RouteEntities.Monetization });

    */
    const engagementHref = `${engagementBasePath}?${queryString}`;
    // const monetizationHref = `${monetizationBasePath}?${queryString}`;

    return (
        <>
            <div className={header}>
                <span>
                    <LensStatsEngagementSection />
                </span>
                <LinkStyled className={linkButtonElevatedClassName} to={engagementHref}>
                    <LensInsightsLinkEngagement />
                </LinkStyled>
            </div>
            <div className={container}>
                <div className={layout}>
                    {stats.map(({ value, label, tooltip }) => (
                        <LensStatsItem key={label} label={label} tooltip={tooltip}>
                            <div className={statValue}>{value}</div>
                        </LensStatsItem>
                    ))}
                </div>
            </div>

            {/* Temporarily hide payout drivers on lens details. */}
            {/*
            <div className={header}>
                <span>
                    <LensStatsMonetizationSection />
                </span>
                {!isRemovedFromLensPlus && (
                    <LinkStyled className={linkButtonElevatedClassName} to={monetizationHref}>
                        <LensInsightsLinkPayout />
                    </LinkStyled>
                )}
            </div>
            {isRemovedFromLensPlus ? (
                <RemovedExclusiveLensAlert />
            ) : (
                <div className={container}>
                    <div className={layout}>
                        <LensStatsItem
                            label={<LensPlusSubscriptionsEarned />}
                            tooltip={<LensPlusSubscriptionsEarnedTooltip />}
                        >
                            <div className={statValue}>{payoutStats.subscriptionsEarned.value}</div>
                        </LensStatsItem>
                        <LensStatsItem label={<TotalLensPlusAudience />} tooltip={<TotalLensPlusAudienceTooltip />}>
                            <div className={statValue}>{payoutStats.audience.value}</div>
                        </LensStatsItem>
                        <LensStatsItem label={<LensPlusScore />} tooltip={<LensPlusScoreTooltip />}>
                            <div className={statValue}>{payoutStats.score.value}</div>
                        </LensStatsItem>
                    </div>
                </div>
            )}
            */}
        </>
    );
};
